import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Calendar, 
  Clock, 
  DollarSign, 
  Users, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ArrowRight,
  ArrowDown,
  TrendingUp,
  Bell,
  Vote,
  Wallet,
  Target,
  Flag,
  RefreshCw,
  ChevronRight,
  Milestone,
  Timer,
  Banknote,
  ShieldCheck,
  UserX,
  UserCheck
} from 'lucide-react';

// Sample data for visualization
const SAMPLE_UNIT = {
  unitId: "UNIT_SKY_1601",
  unitName: "Tower P - Unit 1601",
  projectName: "Skyline Towers",
  bookingDate: "2026-01-21",
  originalAllotmentRate: 12500, // per sqft
  developerCardRate: 15000, // current market rate per sqft
  totalArea: 1200, // sqft
  totalValue: 15000000, // 1.5 Cr
  currentFunding: 5000000, // 50L (first tranche)
  fundedPercent: 33.33,
  investors: [
    { id: "INV_001", name: "Rahul Sharma", ownership: 15, invested: 2250000 },
    { id: "INV_002", name: "Priya Patel", ownership: 10, invested: 1500000 },
    { id: "INV_003", name: "Amit Kumar", ownership: 8.33, invested: 1250000 },
  ]
};

const MILESTONES = [
  { 
    id: "M1", 
    name: "Foundation Complete", 
    tranche: 1, 
    percentage: 33.33,
    estimatedDate: "2026-01-21", 
    actualDate: "2026-01-21",
    status: "COMPLETED",
    fundingRequired: 5000000
  },
  { 
    id: "M2", 
    name: "10th Floor Slab", 
    tranche: 2, 
    percentage: 33.33,
    estimatedDate: "2028-01-21", 
    actualDate: null,
    status: "UPCOMING",
    fundingRequired: 5000000,
    monthsAway: 24,
    lastReviewDate: "2026-01-21",
    reviewHistory: [
      { date: "2026-01-21", monthsAway: 24, notes: "Initial estimate" }
    ]
  },
  { 
    id: "M3", 
    name: "Possession", 
    tranche: 3, 
    percentage: 33.34,
    estimatedDate: "2029-01-21", 
    actualDate: null,
    status: "FUTURE",
    fundingRequired: 5000000,
    monthsAway: 36
  }
];

const CAPITAL_CALL_STATES = {
  NOT_TRIGGERED: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Monitoring' },
  VOTING_OPEN: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Voting Open' },
  TOKEN_COLLECTION: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Token Collection' },
  FUNDING_GAP_FLAGGED: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Gap Flagged' },
  TRANCHE_FUNDED: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Funded' },
};

export default function CapitalCallFlowChart() {
  const [selectedMilestone, setSelectedMilestone] = useState(MILESTONES[1]);
  const [showVotingSimulation, setShowVotingSimulation] = useState(false);

  return (
    <div className="min-h-screen bg-[#050505] text-white p-4 md:p-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <h1 className="text-2xl md:text-3xl font-light tracking-tight mb-2">
          Capital Call Automation Flow
        </h1>
        <p className="text-white/50 text-sm md:text-base">
          Visualizing the automated governance trigger system for construction-linked tranches
        </p>
      </motion.div>

      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* ============================================================ */}
        {/* SECTION 1: LIFECYCLE OVERVIEW CHART */}
        {/* ============================================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6"
        >
          <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
            <Milestone className="w-5 h-5 text-blue-400" />
            Unit Lifecycle: {SAMPLE_UNIT.unitName}
          </h2>

          {/* Timeline Visualization */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute top-8 left-0 right-0 h-1 bg-white/10 rounded-full" />
            <div 
              className="absolute top-8 left-0 h-1 bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all"
              style={{ width: '33%' }}
            />

            {/* Milestones */}
            <div className="relative flex justify-between">
              {MILESTONES.map((milestone, idx) => {
                const isCompleted = milestone.status === 'COMPLETED';
                const isActive = milestone.status === 'UPCOMING';
                
                return (
                  <div 
                    key={milestone.id}
                    className={`flex flex-col items-center cursor-pointer transition-all ${
                      selectedMilestone?.id === milestone.id ? 'scale-105' : ''
                    }`}
                    onClick={() => setSelectedMilestone(milestone)}
                  >
                    {/* Milestone Dot */}
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center z-10 border-4 ${
                      isCompleted 
                        ? 'bg-green-500 border-green-400' 
                        : isActive 
                          ? 'bg-blue-500 border-blue-400 animate-pulse' 
                          : 'bg-[#111] border-white/20'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6 text-white" />
                      ) : (
                        <span className="text-lg font-bold">{idx + 1}</span>
                      )}
                    </div>
                    
                    {/* Milestone Info */}
                    <div className="mt-4 text-center max-w-32">
                      <div className="font-medium text-sm">{milestone.name}</div>
                      <div className="text-xs text-white/50 mt-1">
                        Tranche {milestone.tranche}: {milestone.percentage}%
                      </div>
                      <div className="text-xs text-white/40 mt-1">
                        {isCompleted ? (
                          <span className="text-green-400">✓ Completed</span>
                        ) : (
                          <>Est: {new Date(milestone.estimatedDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</>
                        )}
                      </div>
                      {isActive && (
                        <div className={`mt-2 px-2 py-1 rounded-full text-xs ${
                          milestone.monthsAway <= 12 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {milestone.monthsAway}m away
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quarterly Review Indicator */}
          <div className="mt-8 p-4 bg-[#111] rounded-lg border border-white/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-purple-400" />
                <div>
                  <div className="font-medium text-sm">Quarterly Review Cycle</div>
                  <div className="text-xs text-white/50">Every 3 months - validates estimated dates</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-white/70">Next Review</div>
                <div className="text-purple-400 font-medium">Apr 21, 2026</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ============================================================ */}
        {/* SECTION 2: CAPITAL CALL TRIGGER FLOW */}
        {/* ============================================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6"
        >
          <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
            <Bell className="w-5 h-5 text-yellow-400" />
            Capital Call Trigger Flow (12-Month Rule)
          </h2>

          {/* Flow Chart */}
          <div className="space-y-4">
            {/* Step 1: Detection */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Timer className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex-1 bg-[#111] rounded-lg p-4 border border-blue-500/30">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">1. System Detection</h3>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">AUTOMATIC</span>
                </div>
                <p className="text-sm text-white/60">
                  System monitors milestone dates. When next tranche is <span className="text-yellow-400 font-medium">≤12 months away</span>, 
                  capital call process is automatically triggered.
                </p>
                <div className="mt-3 p-3 bg-[#0A0A0A] rounded border border-white/5">
                  <code className="text-xs text-green-400">
                    if (milestone.monthsAway &lt;= 12 && milestone.status === 'UPCOMING') trigger_capital_call()
                  </code>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-white/30" />
            </div>

            {/* Step 2: Voting Created */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Vote className="w-6 h-6 text-purple-400" />
              </div>
              <div className="flex-1 bg-[#111] rounded-lg p-4 border border-purple-500/30">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">2. Capital Call Voting Created</h3>
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded">51% QUORUM</span>
                </div>
                <p className="text-sm text-white/60 mb-3">
                  Proposal created for all unit investors. Question: <span className="text-white">"Will you fund the upcoming capital call for Tranche 2?"</span>
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="text-green-400 font-medium text-sm mb-1">✓ "I Will Fund"</div>
                    <div className="text-xs text-white/50">Commits to paying token within voting window</div>
                  </div>
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="text-red-400 font-medium text-sm mb-1">✗ "I Opt Out"</div>
                    <div className="text-xs text-white/50">Will not fund, accepts dilution at card rate</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-white/30" />
            </div>

            {/* Step 3: Token Collection (The Carrot) */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Banknote className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="flex-1 bg-[#111] rounded-lg p-4 border border-yellow-500/30">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">3. Token Payment (The Carrot)</h3>
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">ESCROW</span>
                </div>
                <p className="text-sm text-white/60 mb-3">
                  Investors who vote "Fund" must pay a <span className="text-yellow-400 font-medium">token amount</span> within 7 days to lock their commitment.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Pay Token Path */}
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-green-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <UserCheck className="w-5 h-5 text-green-400" />
                      <span className="font-medium text-green-400">Token Paid</span>
                    </div>
                    <ul className="space-y-2 text-xs text-white/60">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Slot reserved at <span className="text-green-400">Original Allotment Rate</span></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>₹{SAMPLE_UNIT.originalAllotmentRate.toLocaleString()}/sqft</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Token sits in escrow until tranche due</span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* No Token Path */}
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-red-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <UserX className="w-5 h-5 text-red-400" />
                      <span className="font-medium text-red-400">No Token / Opt Out</span>
                    </div>
                    <ul className="space-y-2 text-xs text-white/60">
                      <li className="flex items-start gap-2">
                        <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                        <span>Must participate at <span className="text-red-400">Developer Card Rate</span></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                        <span>₹{SAMPLE_UNIT.developerCardRate.toLocaleString()}/sqft</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                        <span>Potential dilution of ownership %</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Price Difference Callout */}
                <div className="mt-4 p-3 bg-gradient-to-r from-green-500/10 to-yellow-500/10 rounded-lg border border-green-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/70">Price Advantage for Token Payers:</span>
                    <span className="text-lg font-bold text-green-400">
                      ₹{((SAMPLE_UNIT.developerCardRate - SAMPLE_UNIT.originalAllotmentRate) * SAMPLE_UNIT.totalArea).toLocaleString()} saved
                    </span>
                  </div>
                  <div className="text-xs text-white/50 mt-1">
                    ({((SAMPLE_UNIT.developerCardRate - SAMPLE_UNIT.originalAllotmentRate) / SAMPLE_UNIT.originalAllotmentRate * 100).toFixed(1)}% discount vs card rate)
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-white/30" />
            </div>

            {/* Step 4: Funding Gap Analysis */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-orange-400" />
              </div>
              <div className="flex-1 bg-[#111] rounded-lg p-4 border border-orange-500/30">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">4. Funding Gap Analysis & Flagging</h3>
                  <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">SYSTEM FLAG</span>
                </div>
                <p className="text-sm text-white/60 mb-3">
                  System calculates commitment shortfall and flags for admin action.
                </p>
                
                {/* Gap Visualization */}
                <div className="bg-[#0A0A0A] rounded-lg p-4 border border-white/5">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-white/50">Tranche 2 Required</span>
                    <span className="font-medium">₹50,00,000</span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-white/70 flex-1">Committed (Token Paid)</span>
                      <span className="text-green-400 font-medium">₹37,50,000 (75%)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span className="text-sm text-white/70 flex-1">Voted Fund, No Token Yet</span>
                      <span className="text-yellow-400 font-medium">₹7,50,000 (15%)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-white/70 flex-1">Opted Out / No Response</span>
                      <span className="text-red-400 font-medium">₹5,00,000 (10%)</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="h-4 bg-white/5 rounded-full overflow-hidden flex">
                    <div className="h-full bg-green-500" style={{ width: '75%' }}></div>
                    <div className="h-full bg-yellow-500" style={{ width: '15%' }}></div>
                    <div className="h-full bg-red-500" style={{ width: '10%' }}></div>
                  </div>

                  {/* Flag Output */}
                  <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Flag className="w-4 h-4 text-orange-400" />
                      <span className="font-medium text-orange-400">Funding Gap Flagged</span>
                    </div>
                    <div className="text-sm text-white/70">
                      <span className="text-orange-400 font-medium">₹5,00,000</span> (10%) needs to be sourced from new investors at developer card rate
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-white/30" />
            </div>

            {/* Step 5: Resolution Paths */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-green-400" />
              </div>
              <div className="flex-1 bg-[#111] rounded-lg p-4 border border-green-500/30">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">5. Resolution & Tranche Funding</h3>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">FINAL</span>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-[#0A0A0A] rounded-lg border border-white/5">
                    <div className="text-2xl mb-2">🎯</div>
                    <div className="font-medium text-sm mb-1">Fill Gap</div>
                    <div className="text-xs text-white/50">Source new investors for unfunded portion at card rate</div>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg border border-white/5">
                    <div className="text-2xl mb-2">💰</div>
                    <div className="font-medium text-sm mb-1">Collect Tranche</div>
                    <div className="text-xs text-white/50">Release escrow tokens + collect remaining amounts</div>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg border border-white/5">
                    <div className="text-2xl mb-2">📊</div>
                    <div className="font-medium text-sm mb-1">Update Ownership</div>
                    <div className="text-xs text-white/50">Recalculate ownership % based on contributions</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ============================================================ */}
        {/* SECTION 3: INVESTOR DECISION MATRIX */}
        {/* ============================================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6"
        >
          <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            Investor Decision Matrix (Sample Scenario)
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-3 text-white/50 font-medium">Investor</th>
                  <th className="text-center p-3 text-white/50 font-medium">Current %</th>
                  <th className="text-center p-3 text-white/50 font-medium">Vote</th>
                  <th className="text-center p-3 text-white/50 font-medium">Token Paid</th>
                  <th className="text-center p-3 text-white/50 font-medium">Rate Applied</th>
                  <th className="text-center p-3 text-white/50 font-medium">New %</th>
                  <th className="text-right p-3 text-white/50 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-3">
                    <div className="font-medium">Rahul Sharma</div>
                    <div className="text-xs text-white/40">INV_001</div>
                  </td>
                  <td className="p-3 text-center">15%</td>
                  <td className="p-3 text-center">
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Fund</span>
                  </td>
                  <td className="p-3 text-center">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mx-auto" />
                  </td>
                  <td className="p-3 text-center text-green-400">₹12,500/sqft</td>
                  <td className="p-3 text-center text-green-400 font-medium">15%</td>
                  <td className="p-3 text-right">
                    <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded text-xs">Locked In</span>
                  </td>
                </tr>
                <tr className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-3">
                    <div className="font-medium">Priya Patel</div>
                    <div className="text-xs text-white/40">INV_002</div>
                  </td>
                  <td className="p-3 text-center">10%</td>
                  <td className="p-3 text-center">
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">Fund</span>
                  </td>
                  <td className="p-3 text-center">
                    <Clock className="w-4 h-4 text-yellow-400 mx-auto" />
                  </td>
                  <td className="p-3 text-center text-white/50">Pending</td>
                  <td className="p-3 text-center text-white/50">TBD</td>
                  <td className="p-3 text-right">
                    <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 rounded text-xs">Awaiting Token</span>
                  </td>
                </tr>
                <tr className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-3">
                    <div className="font-medium">Amit Kumar</div>
                    <div className="text-xs text-white/40">INV_003</div>
                  </td>
                  <td className="p-3 text-center">8.33%</td>
                  <td className="p-3 text-center">
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">Opt Out</span>
                  </td>
                  <td className="p-3 text-center">
                    <XCircle className="w-4 h-4 text-red-400 mx-auto" />
                  </td>
                  <td className="p-3 text-center text-red-400">₹15,000/sqft</td>
                  <td className="p-3 text-center text-red-400 font-medium">6.94%</td>
                  <td className="p-3 text-right">
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 rounded text-xs">Diluted</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Dilution Explanation */}
          <div className="mt-4 p-4 bg-[#111] rounded-lg border border-white/5">
            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              Dilution Calculation for Amit Kumar (Opted Out)
            </h4>
            <div className="text-xs text-white/60 space-y-1">
              <p>• Original ownership: 8.33% = ₹12,50,000 invested at ₹12,500/sqft</p>
              <p>• If he participates in Tranche 2 at card rate: ₹15,000/sqft</p>
              <p>• Same ₹12,50,000 investment now buys less: New ownership ≈ 6.94%</p>
              <p>• <span className="text-red-400">Dilution: -1.39% ownership</span></p>
            </div>
          </div>
        </motion.div>

        {/* ============================================================ */}
        {/* SECTION 4: SYSTEM STATE MACHINE */}
        {/* ============================================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6"
        >
          <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-indigo-400" />
            Capital Call State Machine
          </h2>

          <div className="flex flex-wrap items-center justify-center gap-4">
            {Object.entries(CAPITAL_CALL_STATES).map(([key, state], idx, arr) => (
              <div key={key} className="flex items-center gap-4">
                <div className={`px-4 py-3 rounded-lg ${state.bg} border border-white/10`}>
                  <div className={`font-medium text-sm ${state.text}`}>{state.label}</div>
                  <div className="text-xs text-white/40 mt-1">{key.replace(/_/g, ' ')}</div>
                </div>
                {idx < arr.length - 1 && (
                  <ChevronRight className="w-5 h-5 text-white/20" />
                )}
              </div>
            ))}
          </div>

          {/* Triggers Summary */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-[#111] rounded-lg border border-white/5 text-center">
              <div className="text-2xl mb-2">📅</div>
              <div className="font-medium text-sm">12 Months</div>
              <div className="text-xs text-white/50">Trigger threshold</div>
            </div>
            <div className="p-4 bg-[#111] rounded-lg border border-white/5 text-center">
              <div className="text-2xl mb-2">🗳️</div>
              <div className="font-medium text-sm">51% Quorum</div>
              <div className="text-xs text-white/50">For routine calls</div>
            </div>
            <div className="p-4 bg-[#111] rounded-lg border border-white/5 text-center">
              <div className="text-2xl mb-2">⏱️</div>
              <div className="font-medium text-sm">7 Days</div>
              <div className="text-xs text-white/50">Token payment window</div>
            </div>
            <div className="p-4 bg-[#111] rounded-lg border border-white/5 text-center">
              <div className="text-2xl mb-2">🔄</div>
              <div className="font-medium text-sm">3 Months</div>
              <div className="text-xs text-white/50">Review cycle</div>
            </div>
          </div>
        </motion.div>

        {/* ============================================================ */}
        {/* SECTION 5: EXIT VOTING (75% Quorum) */}
        {/* ============================================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6"
        >
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            Exit Voting (75% Quorum) - LLP Agreement Clause
          </h2>
          
          <div className="p-4 bg-[#111] rounded-lg border border-amber-500/30 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-amber-400 mb-1">5% Per Annum Floor Clause</div>
                <p className="text-sm text-white/60">
                  If unit value appreciation is <span className="text-red-400 font-medium">below 5% per annum</span> from allotment date, 
                  exit voting requires <span className="text-amber-400 font-medium">75% ownership approval</span>. 
                  This protects minority investors from forced exit at unfavorable terms.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/20">
              <h4 className="font-medium text-green-400 mb-2">Exit Above 5% Appreciation</h4>
              <ul className="text-sm text-white/60 space-y-1">
                <li>• Standard 51% quorum applies</li>
                <li>• Simple majority can trigger exit</li>
                <li>• Faster decision process</li>
              </ul>
            </div>
            <div className="p-4 bg-red-500/5 rounded-lg border border-red-500/20">
              <h4 className="font-medium text-red-400 mb-2">Exit Below 5% Appreciation</h4>
              <ul className="text-sm text-white/60 space-y-1">
                <li>• 75% quorum required</li>
                <li>• Protects minority from bad exits</li>
                <li>• Forces better negotiation or hold</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Legend */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-white/40 text-sm pb-8"
        >
          This flow chart visualizes the automated capital call governance system. 
          Confirm the logic and I'll build out the complete backend automation.
        </motion.div>
      </div>
    </div>
  );
}
